/* The consequence preview: what will actually happen if you play this move. */
import { WHITE } from '../../../packages/engine/src/index.ts';
import type { Event, LegalMove, Position, Side } from '../../../packages/engine/src/index.ts';

export type Consequence = 'quiet' | 'push' | 'bond' | 'grab' | 'capture' | 'danger';

export interface Preview {
  move: LegalMove;
  cell: number;
  kind: Consequence;
  /** a capture that also costs you a piece */
  alsoLoses: boolean;
  text: string;
}

const plural = (n: number, one: string, many = one + 's'): string => `${n} ${n === 1 ? one : many}`;

export function summarise(pos: Position, events: Event[], me: Side): { kind: Consequence; alsoLoses: boolean; text: string } {
  let enemyOff = 0, selfOff = 0, grab = 0, friendly = 0, pushes = 0;
  for (const e of events) {
    switch (e.kind) {
      case 'pushed-off': case 'drag-off':
        if (pos.owner[e.piece] === me) selfOff++; else enemyOff++;
        break;
      case 'bond':
        if (pos.owner[e.a] === pos.owner[e.b]) { if (pos.owner[e.a] === me) friendly++; } else grab++;
        break;
      case 'push':
        pushes += e.body.length;
        break;
      default:
        break;
    }
  }
  const parts: string[] = [];
  if (enemyOff) parts.push(`knocks ${plural(enemyOff, 'enemy piece')} off`);
  if (selfOff) parts.push(`loses ${plural(selfOff, 'piece')} of yours`);
  if (grab) parts.push(`bonds to ${plural(grab, 'enemy piece')}`);
  if (friendly) parts.push(`joins ${plural(friendly, 'piece')} of yours`);
  if (pushes) parts.push(`shoves ${plural(pushes, 'piece')}`);
  const kind: Consequence = enemyOff ? 'capture' : selfOff ? 'danger' : grab ? 'grab' : friendly ? 'bond' : pushes ? 'push' : 'quiet';
  return { kind, alsoLoses: enemyOff > 0 && selfOff > 0, text: parts.length ? parts.join(', ') : 'nothing moves' };
}

/** Previews for one piece, split by whether the piece moves with its body or alone. */
export function previewsFor(moves: LegalMove[], piece: number, solo: boolean, me: Side): Preview[] {
  const out: Preview[] = [];
  for (const lm of moves) {
    if (lm.move.piece !== piece || lm.move.solo !== solo) continue;
    const first = lm.events[0];
    if (!first || first.kind !== 'move') continue;
    const { kind, alsoLoses, text } = summarise(lm.pos, lm.events, me);
    out.push({ move: lm, cell: first.to, kind, alsoLoses, text });
  }
  return out;
}

export const sideName = (s: Side): string => (s === WHITE ? 'White' : 'Black');
